import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MaterialsPicker from "@/components/MaterialsPicker";
import MaterialsUsedPicker from "@/components/MaterialsUsedPicker";
import ChargeCodesPicker from "@/components/ChargeCodesPicker";
import TechMultiSelect from "@/components/TechMultiSelect";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";

export default function AdminEditJob({ open, setOpen, job, onSaved }) {
  const [f, setF] = useState({});
  const [docketCodes, setDocketCodes] = useState([]); // [{index, docket_number, visit_date, charge_codes}]
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    setF({
      description_of_works: job.description_of_works || "", internal_notes: job.internal_notes || "",
      customer_comments: job.customer_comments || "", customer_name: job.customer_name || "",
      builder: job.builder || "", contact_person: job.contact_person || "",
      contact_position: job.contact_position || "", contact_email: job.contact_email || "",
      technician_ids: job.technician_ids && job.technician_ids.length ? job.technician_ids : (job.technician_id ? [job.technician_id] : []),
      start_time: job.start_time || "", finish_time: job.finish_time || "",
      appointment_date: job.appointment_date || "", site_address: job.site_address || "", phone: job.phone || "",
      materials_used: job.materials_used || [], charge_codes: job.charge_codes || [],
      materials_consumed: (job.materials_consumed || []).map((m) => ({ material_id: m.material_id, product_name: m.product_name, unit: m.unit, quantity: m.quantity ?? "" })),
      billing_notes: job.billing_notes || "",
    });
    const dockets = job.dockets || [];
    setDocketCodes(dockets.map((d, i) => ({
      index: i, docket_number: d.docket_number, visit_date: d.visit_date,
      charge_codes: (d.charge_codes || []).map((c) => ({ ...c, rate: c.rate ?? "" })),
    })));
    api.get("/assignable-workers").then((r) => setTechs(r.data)).catch(() => {});
  }, [open, job]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setDocketCC = (i, codes) => setDocketCodes((prev) => prev.map((d) => (d.index === i ? { ...d, charge_codes: codes } : d)));
  const hasDockets = docketCodes.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      payload.materials_consumed = (f.materials_consumed || [])
        .filter((m) => m.material_id && m.quantity !== "" && Number(m.quantity) > 0)
        .map((m) => ({ material_id: m.material_id, product_name: m.product_name, unit: m.unit, quantity: Number(m.quantity) }));
      if (hasDockets) {
        // Charge codes live on dockets (the invoice's pricing source) — send those, not job-level.
        delete payload.charge_codes;
        payload.dockets_charge_codes = docketCodes.map((d) => ({ index: d.index, charge_codes: d.charge_codes }));
      }
      await api.patch(`/jobs/${job.id}/admin-edit`, payload);
      toast.success("Changes saved (audit recorded). Any draft invoice was refreshed.");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit Completed Docket (Admin)</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-400 -mt-2">Every change is recorded in the audit history (admin, date, old → new). Nothing is deleted. Draft invoices auto-refresh.</DialogDescription>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer</Label><Input data-testid="ae-customer-name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1" /></div>
            <div><Label>Builder</Label><Input data-testid="ae-builder" value={f.builder} onChange={(e) => set("builder", e.target.value)} className="mt-1" /></div>
            <div><Label>Contact Person</Label><Input data-testid="ae-contact-person" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="mt-1" /></div>
            <div><Label>Contact Position</Label><Input value={f.contact_position} onChange={(e) => set("contact_position", e.target.value)} className="mt-1" /></div>
            <div><Label>Contact Email</Label><Input value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label>Site Address</Label><Input data-testid="ae-site-address" value={f.site_address} onChange={(e) => set("site_address", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Technician Allocation</Label>
              <TechMultiSelect techs={techs} value={f.technician_ids} onChange={(v) => set("technician_ids", v)} testid="ae-technician" />
            </div>
            <div><Label>Visit Date</Label><Input type="date" value={f.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} className="mt-1" /></div>
            <div><Label>Arrival Time</Label><Input data-testid="ae-arrival" type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className="mt-1" /></div>
            <div><Label>Finish Time</Label><Input data-testid="ae-finish" type="time" value={f.finish_time} onChange={(e) => set("finish_time", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label>Job Description</Label><Textarea data-testid="ae-description" value={f.description_of_works} onChange={(e) => set("description_of_works", e.target.value)} rows={3} className="mt-1" /></div>
          <div><Label>Daily Notes (Customer Comments)</Label><Textarea value={f.customer_comments} onChange={(e) => set("customer_comments", e.target.value)} rows={2} className="mt-1" /></div>
          <div><Label>Internal Notes</Label><Textarea data-testid="ae-internal-notes" value={f.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} rows={2} className="mt-1" /></div>
          <div><Label>Billing Notes <span className="text-slate-400 font-normal">(office use only)</span></Label><Textarea data-testid="ae-billing-notes" value={f.billing_notes} onChange={(e) => set("billing_notes", e.target.value)} rows={2} className="mt-1" /></div>
          <div><Label>Sealant Products & Colours</Label><div className="mt-1"><MaterialsPicker value={f.materials_used} onChange={(v) => set("materials_used", v)} /></div></div>
          <div><Label>Materials Used <span className="text-slate-400 font-normal">(internal costing — not shown on the customer invoice)</span></Label><div className="mt-1"><MaterialsUsedPicker value={f.materials_consumed} onChange={(v) => set("materials_consumed", v)} /></div></div>

          {hasDockets ? (
            <div>
              <Label>Charge Codes, Quantities & Rates <span className="text-slate-400 font-normal">(per docket · Admin rate override)</span></Label>
              <div className="space-y-3 mt-1">
                {docketCodes.map((d) => (
                  <div key={d.index} data-testid={`ae-docket-cc-${d.index}`} className="border border-slate-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-navy mb-2">Docket {d.docket_number || d.index + 1}{d.visit_date ? ` · ${formatDate(d.visit_date)}` : ""}</p>
                    <ChargeCodesPicker value={d.charge_codes} onChange={(v) => setDocketCC(d.index, v)} showRate jobId={job?.id} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div><Label>Charge Codes, Quantities & Rates <span className="text-slate-400 font-normal">(Admin rate override)</span></Label>
              <div className="mt-1"><ChargeCodesPicker value={f.charge_codes} onChange={(v) => set("charge_codes", v)} showRate jobId={job?.id} /></div>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="ae-save" onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light">{saving ? "Saving…" : "Save Changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
